import { API_BASE_URL } from './config';
import { ApiError, ApiNetworkError, ApiTimeoutError, toProblemDetails } from './errors';

export type QueryValue =
  | string
  | number
  | boolean
  | undefined
  /** Repeated as `?k=a&k=b` — how FastAPI reads a list-valued query parameter. */
  | readonly (string | number)[];

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised as JSON unless it is already a `BodyInit`. */
  body?: unknown;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  /** Caller-owned cancellation; composed with the client's own timeout. */
  signal?: AbortSignal;
  /** Overrides `defaultTimeoutMs` for this call. */
  timeoutMs?: number;
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Injectable for tests and for server-side rendering. */
  fetchImpl?: typeof fetch;
  defaultTimeoutMs?: number;
  /**
   * Supplies the current bearer token (a dev token in AUTH_MODE=dev, or a real
   * Entra access token in prod). Resolved per call so a fresh token is always
   * used; returning null sends no Authorization header (e.g. the pre-auth
   * branding-resolve call).
   */
  getAuthToken?: () => string | null;
  /**
   * Called once when any request comes back 401 — the session drops to /login.
   * The backend, not the client, decides a token is invalid.
   */
  onUnauthorized?: () => void;
  /**
   * Tries to renew the session once on a 401, before falling back to
   * `onUnauthorized`. Returns the new access token to retry the request with,
   * or `null` if renewal isn't possible/failed (AUTH_MODE=dev has no refresh
   * concept, a dev-mode client simply never sets this option). Never thrown
   * from — a rejected promise is treated the same as `null`.
   */
  refreshToken?: () => Promise<string | null>;
}

/** Payload of `GET /healthz` on the Docsight API. */
export interface HealthResponse {
  status: string;
  service?: string;
  version?: string;
}

const DEFAULT_TIMEOUT_MS = 15_000;

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof Blob ||
    value instanceof ArrayBuffer
  );
}

function buildUrl(baseUrl: string, path: string, query: Record<string, QueryValue> | undefined): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${baseUrl}${suffix}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      // `append`, not `set`: each entry is its own occurrence of the key, which
      // is what a list-valued parameter means on the wire.
      for (const entry of value) url.searchParams.append(key, String(entry));
      continue;
    }
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function decodeJson(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return undefined;
  const text = await response.text();
  if (text.length === 0) return undefined;
  return JSON.parse(text) as unknown;
}

/**
 * Typed `fetch` wrapper for the Docsight backend.
 *
 * Every call is JSON in / JSON out, carries a deadline, honours a caller
 * `AbortSignal`, and converts a non-2xx response into an {@link ApiError} that
 * still carries the server's problem-details body.
 */
export class ApiClient {
  readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch | undefined;
  private readonly defaultTimeoutMs: number;
  private readonly getAuthToken: (() => string | null) | undefined;
  private readonly onUnauthorized: (() => void) | undefined;
  private readonly refreshToken: (() => Promise<string | null>) | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? API_BASE_URL).replace(/\/+$/, '');
    // Resolved per call, never captured: `globalThis.fetch` may legitimately be
    // replaced after construction (service workers, request interceptors).
    this.fetchImpl = options.fetchImpl;
    this.defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.refreshToken = options.refreshToken;
  }

  /** Tries `refreshToken` once; a throw is treated the same as `null`. */
  private async tryRenew(): Promise<string | null> {
    if (!this.refreshToken) return null;
    try {
      return await this.refreshToken();
    } catch {
      return null;
    }
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, query, headers, signal, timeoutMs = this.defaultTimeoutMs } = options;
    const url = buildUrl(this.baseUrl, path, query);

    if (signal?.aborted === true) {
      throw signal.reason as Error;
    }

    const requestHeaders = new Headers({ Accept: 'application/json', ...headers });
    const token = this.getAuthToken?.() ?? null;
    if (token !== null && !requestHeaders.has('Authorization')) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }
    let payload: BodyInit | undefined;
    if (body !== undefined) {
      if (isBodyInit(body)) {
        payload = body;
      } else {
        payload = JSON.stringify(body);
        if (!requestHeaders.has('Content-Type')) {
          requestHeaders.set('Content-Type', 'application/json');
        }
      }
    }

    // One controller composes both cancellation sources per attempt. Each
    // aborts with a reason, so the catch below can tell a deadline from a
    // caller cancel. A fresh controller/timer per `send()` call, so a retry
    // after a token refresh gets its own full timeout budget rather than
    // whatever was left of the first attempt's.
    const send = async (): Promise<Response> => {
      const controller = new AbortController();
      const timer = setTimeout(() => {
        controller.abort(new ApiTimeoutError(timeoutMs, url));
      }, timeoutMs);
      const forwardAbort = (): void => {
        controller.abort(signal?.reason);
      };
      signal?.addEventListener('abort', forwardAbort, { once: true });
      try {
        const doFetch = this.fetchImpl ?? globalThis.fetch;
        return await doFetch(url, {
          method,
          headers: requestHeaders,
          signal: controller.signal,
          ...(payload === undefined ? {} : { body: payload }),
        });
      } catch (cause) {
        if (controller.signal.aborted) {
          throw controller.signal.reason as Error;
        }
        throw new ApiNetworkError(url, cause);
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', forwardAbort);
      }
    };

    let response = await send();

    // One renewal attempt on a 401, before treating it as final. A dev-mode
    // client has no `refreshToken` configured, so `tryRenew` is a no-op there.
    if (response.status === 401) {
      const renewed = await this.tryRenew();
      if (renewed !== null) {
        requestHeaders.set('Authorization', `Bearer ${renewed}`);
        response = await send();
      }
    }

    const decoded = await decodeJson(response);

    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.();
      const problem = toProblemDetails(decoded);
      const message =
        problem?.detail ??
        problem?.title ??
        `${String(response.status)} ${response.statusText || 'Request failed'}`;
      throw new ApiError(message, response.status, problem, url);
    }

    return decoded as T;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * `GET` a binary response as a `Blob`, carrying the same bearer token, timeout
   * and 401 handling as every other call.
   *
   * Needed because an authenticated image cannot be fetched by putting the URL
   * in `<img src>` — the browser sends no `Authorization` header there, so the
   * request would come back 401. The caller turns the blob into an object URL
   * and is responsible for revoking it.
   */
  async getBlob(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
    const { query, headers, signal, timeoutMs = this.defaultTimeoutMs } = options;
    const url = buildUrl(this.baseUrl, path, query);

    if (signal?.aborted === true) throw signal.reason as Error;

    const requestHeaders = new Headers({ Accept: 'image/*', ...headers });
    const token = this.getAuthToken?.() ?? null;
    if (token !== null && !requestHeaders.has('Authorization')) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }

    const send = async (): Promise<Response> => {
      const controller = new AbortController();
      const timer = setTimeout(() => {
        controller.abort(new ApiTimeoutError(timeoutMs, url));
      }, timeoutMs);
      const forwardAbort = (): void => {
        controller.abort(signal?.reason);
      };
      signal?.addEventListener('abort', forwardAbort, { once: true });
      try {
        const doFetch = this.fetchImpl ?? globalThis.fetch;
        return await doFetch(url, { method: 'GET', headers: requestHeaders, signal: controller.signal });
      } catch (cause) {
        if (controller.signal.aborted) throw controller.signal.reason as Error;
        throw new ApiNetworkError(url, cause);
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', forwardAbort);
      }
    };

    let response = await send();

    if (response.status === 401) {
      const renewed = await this.tryRenew();
      if (renewed !== null) {
        requestHeaders.set('Authorization', `Bearer ${renewed}`);
        response = await send();
      }
    }

    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.();
      throw new ApiError(
        `${String(response.status)} ${response.statusText || 'Request failed'}`,
        response.status,
        null,
        url,
      );
    }
    return response.blob();
  }

  /** Liveness probe of the backend (`GET /healthz`). */
  health(options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<HealthResponse> {
    return this.get<HealthResponse>('/healthz', options);
  }
}

/** The app-wide client, pointed at `VITE_API_BASE_URL`. */
export const apiClient = new ApiClient();
